import type * as React from 'react'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useEffect, useState } from 'react'
import { Card, Flex, Heading, SimpleSpinner, Text } from '@buffer-mono/popcorn'

import { graphql } from '../../../../../gql'

import styles from '../index.module.css'
import { CancelFlowStepFrame } from './CancelFlowStepFrame'

const METRIC_LOCALE = 'en-US'

const metricFormatter = new Intl.NumberFormat(METRIC_LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export const CANCEL_FLOW_ORG_ACTIVITY_SUMMARY = graphql(/* GraphQL */ `
  query CancelFlowOrgActivitySummary($organizationId: OrganizationId!) {
    organizationActivitySummary(input: { organizationId: $organizationId }) {
      __typename
      ... on OrganizationActivitySummary {
        postsPublished {
          total
        }
        ideasCreated {
          total
        }
        commentReplies {
          total
        }
        followers {
          current
        }
        engagement {
          current
        }
        weeklyPostingStreak {
          weeks
        }
      }
    }
  }
`)

type ActivitySummaryPayload =
  | ResultOf<
      typeof CANCEL_FLOW_ORG_ACTIVITY_SUMMARY
    >['organizationActivitySummary']
  | null
  | undefined

export function getActivityStats(activityPayload: ActivitySummaryPayload): {
  posts: number | null
  ideas: number | null
  comments: number | null
  followers: number | null
  engagement: number | null
  streak: number | null
} {
  const activity =
    activityPayload?.__typename === 'OrganizationActivitySummary'
      ? activityPayload
      : null

  return {
    posts: activity?.postsPublished?.total ?? null,
    ideas: activity?.ideasCreated?.total ?? null,
    comments: activity?.commentReplies?.total ?? null,
    followers: activity?.followers?.current ?? null,
    engagement: activity?.engagement?.current ?? null,
    streak: activity?.weeklyPostingStreak?.weeks ?? null,
  }
}

export function hasActivityStats(
  activityPayload: ActivitySummaryPayload,
): boolean {
  const { posts, ideas, comments, followers, engagement, streak } =
    getActivityStats(activityPayload)

  return [posts, ideas, comments, followers, engagement, streak].some(
    (value) => value != null && value > 0,
  )
}

export function isDefaultActivityState(
  activityPayload: ActivitySummaryPayload,
): boolean {
  return (
    activityPayload?.__typename === 'OrganizationActivitySummaryDefaultState'
  )
}

function getPrefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
  )
}

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    getPrefersReducedMotion,
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mediaQuery) return

    const handleChange = (): void => {
      setPrefersReducedMotion(mediaQuery.matches)
    }

    mediaQuery.addEventListener?.('change', handleChange)
    return (): void => {
      mediaQuery.removeEventListener?.('change', handleChange)
    }
  }, [])

  return prefersReducedMotion
}

function getComparisonPlanName(planName: string): string | null {
  const normalizedPlanName = planName.trim().toLowerCase()

  if (normalizedPlanName.includes('team')) return 'Team'
  if (normalizedPlanName.includes('essentials')) return 'Essentials'

  return null
}

function useCountUp(
  target: number,
  duration = 900,
  prefersReducedMotion = false,
): number {
  const [count, setCount] = useState(() => (prefersReducedMotion ? target : 0))
  useEffect(() => {
    if (target === 0 || prefersReducedMotion) {
      setCount(target)
      return
    }
    setCount(0)
    const startTime = performance.now()
    let raf: number
    const animate = (now: number): void => {
      const progress = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.round(eased * target))
      if (progress < 1) {
        raf = requestAnimationFrame(animate)
      }
    }
    raf = requestAnimationFrame(animate)
    return (): void => cancelAnimationFrame(raf)
  }, [target, duration, prefersReducedMotion])
  return count
}

type LossAversionStepProps = {
  activitySummary: ActivitySummaryPayload
  isLoadingStats: boolean
  hasMoreSelectableChannelsThanFreePlan: boolean
  planName: string
  stepLabel?: string
  isTrialCancellation?: boolean
  onContinue: () => void
  onKeep: () => void
}

export const LossAversionStep = ({
  activitySummary,
  isLoadingStats,
  hasMoreSelectableChannelsThanFreePlan,
  planName,
  stepLabel,
  isTrialCancellation = false,
  onContinue,
  onKeep,
}: LossAversionStepProps): React.ReactElement => {
  const { posts, ideas, comments, followers, engagement, streak } =
    getActivityStats(activitySummary)
  const prefersReducedMotion = usePrefersReducedMotion()

  const animatedPosts = useCountUp(posts ?? 0, 900, prefersReducedMotion)
  const animatedIdeas = useCountUp(ideas ?? 0, 900, prefersReducedMotion)
  const animatedComments = useCountUp(comments ?? 0, 900, prefersReducedMotion)
  const animatedFollowers = useCountUp(
    followers ?? 0,
    900,
    prefersReducedMotion,
  )
  const animatedEngagement = useCountUp(
    engagement ?? 0,
    900,
    prefersReducedMotion,
  )
  const animatedStreak = useCountUp(streak ?? 0, 900, prefersReducedMotion)

  const formatMetric = (value: number): string => metricFormatter.format(value)

  const smallStats: { value: string; label: string }[] = [
    comments != null &&
      comments > 0 && {
        value: formatMetric(animatedComments),
        label: 'Comments replied to',
      },
    followers != null &&
      followers > 0 && {
        value: formatMetric(animatedFollowers),
        label: 'Followers',
      },
    engagement != null &&
      engagement > 0 && {
        value: formatMetric(animatedEngagement),
        label: '30-day engagements',
      },
    streak != null &&
      streak > 0 && {
        value: `${formatMetric(animatedStreak)} wk`,
        label: 'Posting streak',
      },
  ].filter((s): s is { value: string; label: string } => s !== false)

  const hasLargeStats =
    (posts != null && posts > 0) || (ideas != null && ideas > 0)
  const hasAnyStats = hasLargeStats || smallStats.length > 0
  const showDefaultState = isDefaultActivityState(activitySummary)
  const showStatsLoader = isLoadingStats && !hasAnyStats && !showDefaultState
  const currentPlanName = getComparisonPlanName(planName)
  const currentPlanDescription = currentPlanName
    ? `You’re currently on ${currentPlanName}.`
    : 'You’re currently on a paid plan.'
  const currentPlanColumnLabel = currentPlanName ?? 'Current plan'
  const currentPlanChannelAccess = hasMoreSelectableChannelsThanFreePlan
    ? '4+ channels'
    : 'Up to 3 channels'

  return (
    <CancelFlowStepFrame
      secondaryAction={{
        children: isTrialCancellation ? 'Keep My Trial' : 'Keep My Plan',
        onClick: onKeep,
        variant: 'tertiary',
      }}
      primaryAction={{
        children: isTrialCancellation
          ? 'Continue Cancelling Trial'
          : 'Continue Cancellation',
        disabled: showStatsLoader,
        onClick: onContinue,
        variant: 'critical',
      }}
      stepLabel={stepLabel}
      title={
        showDefaultState
          ? 'Here’s What Changes on the Free Plan'
          : isTrialCancellation
            ? 'Are You Sure You Want to Cancel Your Trial?'
            : 'Are You Sure You Want to Cancel?'
      }
      description={
        showDefaultState
          ? `${currentPlanDescription} If you continue, your account will move to Buffer’s Free plan.`
          : isTrialCancellation
            ? 'You’ve already made progress with Buffer.'
            : hasAnyStats || showStatsLoader
              ? "You've put these features to good use."
              : 'Review what will change before you continue cancelling your Buffer plan.'
      }
      descriptionProps={{ color: 'subtle' }}
      descriptionWrapperClassName={
        showDefaultState ? styles.defaultStateDescription : undefined
      }
      introClassName={styles.dataText}
      contentClassName={styles.dataBody}
    >
      {showStatsLoader && (
        <div
          className={`${styles.dataStats} ${styles.dataStatsLoading}`}
          role="status"
          aria-live="polite"
          aria-label="Loading your activity"
        >
          <SimpleSpinner color="brand" />
        </div>
      )}
      {showDefaultState && (
        <div className={styles.planComparison}>
          <Card padding="lg">
            <table className={styles.planComparisonTable}>
              <caption className={styles.visuallyHidden}>
                Plan comparison after cancellation
              </caption>
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">{currentPlanColumnLabel}</th>
                  <th scope="col">Free</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Channels</th>
                  <td>{currentPlanChannelAccess}</td>
                  <td>Up to 3 channels</td>
                </tr>
                <tr>
                  <th scope="row">Posts</th>
                  <td>Unlimited posts</td>
                  <td>10 posts</td>
                </tr>
                <tr>
                  <th scope="row">Ideas</th>
                  <td>Unlimited ideas</td>
                  <td>100 ideas</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      )}
      {hasAnyStats && (
        <div className={styles.dataStats}>
          <div className={styles.statsGrid}>
            {posts != null && posts > 0 && (
              <Card padding="lg">
                <Flex direction="column" gap="2xs">
                  <Heading as="p" size="xl" className={styles.statNumber}>
                    {formatMetric(animatedPosts)}
                  </Heading>
                  <Text color="subtle">Posts published</Text>
                </Flex>
              </Card>
            )}
            {ideas != null && ideas > 0 && (
              <Card padding="lg">
                <Flex direction="column" gap="2xs">
                  <Heading as="p" size="xl" className={styles.statNumber}>
                    {formatMetric(animatedIdeas)}
                  </Heading>
                  <Text color="subtle">Ideas created</Text>
                </Flex>
              </Card>
            )}
            {smallStats.map((stat) => (
              <Card key={stat.label} padding="lg">
                <Flex direction="column" gap="2xs">
                  <Heading as="p" size="xl" className={styles.statNumber}>
                    {stat.value}
                  </Heading>
                  <Text color="subtle">{stat.label}</Text>
                </Flex>
              </Card>
            ))}
          </div>
        </div>
      )}
    </CancelFlowStepFrame>
  )
}
